import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../hooks/AppContext';
import { api } from '../utils/api';
import { fmt, statusBadge, ISSUES, getCurrentPayMonth, getRentMonth } from '../utils/helpers';

export default function ManagerView() {
  const { user, activeMonth } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState({});
  const [flagModal, setFlagModal] = useState(null);  // godown key
  const [flagSelections, setFlagSelections] = useState({});
  const [submitModal, setSubmitModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getGodowns(activeMonth);
      setData(d);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [activeMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading || !data) return <div className="wrap"><div className="loading">Loading...</div></div>;

  const rows = data.data;
  const month = data.month;
  const cutoff = data.cutoff;

  // Totals — a row counts once it is either already confirmed in the database
  // or currently ticked, so the footer tracks the checkboxes live.
  let yesAmt = 0, noAmt = 0, yesCount = 0, noCount = 0;
  let selCount = 0, selRent = 0, selTds = 0, selGst = 0, selDed = 0, selNet = 0;
  rows.forEach(r => {
    const key = `${r.godown_id}_${r.vendor_id}`;
    const isSelected = r.status === 'yes' || (r.status === 'pending' && !!checks[key]);
    if (isSelected) {
      selCount++;
      selRent += Number(r.monthly_rent) || 0;
      selTds += Number(r.tds) || 0;
      selGst += (Number(r.sgst)||0) + (Number(r.cgst)||0) + (Number(r.igst)||0) + (Number(r.utgst)||0);
      selDed += Number(r.mded) || 0;
      selNet += Number(r.net_amt) || 0;
    }
    if (r.status === 'yes') { yesAmt += Number(r.net_amt); }
    else if (r.status === 'pending') {
      if (checks[key]) { yesAmt += Number(r.net_amt); yesCount++; }
      else { noAmt += Number(r.net_amt); noCount++; }
    }
  });
  const confirmedCount = selCount;

  const toggleCheck = (key) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));
  const selectAll = (checked) => {
    const next = {};
    rows.forEach(r => {
      if (r.status === 'pending') next[`${r.godown_id}_${r.vendor_id}`] = checked;
    });
    setChecks(prev => ({ ...prev, ...next }));
  };

  const openFlagModal = (r) => {
    const key = `${r.godown_id}_${r.vendor_id}`;
    setFlagModal(key);
    const existing = r.confirmation?.issues || [];
    const sel = {};
    existing.forEach(iss => { const idx = ISSUES.indexOf(iss); if (idx >= 0) sel[idx] = true; });
    setFlagSelections(sel);
  };

  const saveFlagIssues = async () => {
    const issues = Object.keys(flagSelections).filter(k => flagSelections[k]).map(k => ISSUES[parseInt(k)]);
    // Issues are saved with the confirmation on submit; store locally for now
    const r = rows.find(r => `${r.godown_id}_${r.vendor_id}` === flagModal);
    if (r) {
      if (!r.confirmation) r.confirmation = { issues: [] };
      r.confirmation.issues = issues;
    }
    setFlagModal(null);
  };

  const handleSubmit = () => {
    const pending = rows.filter(r => r.status === 'pending');
    if (pending.length === 0) { alert('No pending godowns.'); return; }
    let yC = 0, nC = 0, yA = 0, nA = 0;
    pending.forEach(r => {
      const key = `${r.godown_id}_${r.vendor_id}`;
      if (checks[key]) { yC++; yA += Number(r.net_amt); }
      else { nC++; nA += Number(r.net_amt); }
    });
    setSubmitModal({ yC, nC, yA, nA });
  };

  const confirmSubmit = async () => {
    setSubmitting(true);
    try {
      const pending = rows.filter(r => r.status === 'pending');
      const confirmations = pending.map(r => {
        const key = `${r.godown_id}_${r.vendor_id}`;
        return {
          godownId: r.godown_id,
          vendorId: r.vendor_id,
          valid: !!checks[key],
          issues: r.confirmation?.issues || [],
        };
      });
      await api.saveConfirmations(confirmations);
      setSubmitModal(null);
      setChecks({});
      loadData();
    } catch (err) {
      alert(err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="wrap">
      <div className="page-header">
        <div>
          <h2>Godown Rent Confirmation</h2>
          <p className="sub">CBS Station: <strong>{user.cbsbrcd}</strong></p>
        </div>
      </div>

      {/* Cutoff banner */}
      {cutoff && (
        <div className={`cutoff-banner ${cutoff.isExpired ? 'expired' : ''}`}>
          <span>⏰ Cut-off: {new Date(cutoff.datetime).toLocaleString('en-IN')}</span>
          <span>For: {month}</span>
        </div>
      )}

      {/* Info bar */}
      <div className="info-bar">
        <div><div className="ib-label">Payment Month</div><div className="ib-val">{month}</div></div>
        <div><div className="ib-label">Total Godowns</div><div className="ib-val">{rows.length}</div></div>
        <div><div className="ib-label">Confirmed</div><div className="ib-val text-green">{confirmedCount} / {rows.length}</div></div>
      </div>

      {/* Select All */}
      {rows.some(r => r.status === 'pending') && (
        <label className="select-all">
          <input type="checkbox" onChange={e => selectAll(e.target.checked)} /> Select All
        </label>
      )}

      {/* Table */}
      <div className="tbl-wrap">
        <table>
          <thead><tr>
            <th>Acode</th><th>Godown</th><th>Valid</th><th>Owner</th>
            <th className="text-right">Rent (₹)</th><th className="text-right">TDS</th>
            <th className="text-right">GST</th><th className="text-right">M.DED</th>
            <th className="text-right">Net Amt</th>
            <th>Rent Type</th><th>Bank</th><th>Confirmed</th><th>Report Issue</th>
          </tr></thead>
          <tbody>
            {rows.map(r => {
              const key = `${r.godown_id}_${r.vendor_id}`;
              const locked = r.status !== 'pending';
              const isChecked = checks[key];
              const issues = r.confirmation?.issues || [];
              const gstTotal = (Number(r.sgst)||0) + (Number(r.cgst)||0) + (Number(r.igst)||0) + (Number(r.utgst)||0);
              return (
                <tr key={key} className={r.status === 'yes' ? 'row-yes' : r.status === 'expired' ? 'row-expired' : isChecked ? 'row-yes' : ''}>
                  <td><strong>{r.acode}</strong></td>
                  <td>
                    <div className="fw-600">{r.godown_name}</div>
                    <div className="text-sm text-muted">{r.gm_name} · #{r.godown_id}</div>
                  </td>
                  <td className="text-center">
                    {locked ? statusBadge(r.status) :
                      <input type="checkbox" checked={!!isChecked} onChange={() => toggleCheck(key)} />
                    }
                  </td>
                  <td>
                    <div>{r.vendor_name}</div>
                    <div className="mono text-sm">PAN: {r.pan_no}</div>
                  </td>
                  <td className="text-right fw-600">₹{fmt(r.monthly_rent)}</td>
                  <td className="text-right" style={{color: r.tds > 0 ? 'var(--red)' : 'var(--gry)'}}>
                    {r.tds > 0 ? `₹${fmt(r.tds)}` : '—'}
                  </td>
                  <td className="text-right" style={{color: gstTotal > 0 ? 'var(--amb)' : 'var(--gry)'}}>
                    {gstTotal > 0 ? `₹${fmt(gstTotal)}` : '—'}
                  </td>
                  <td className="text-right" style={{color: r.mded > 0 ? 'var(--red)' : 'var(--gry)'}}>
                    {r.mded > 0 ? `₹${fmt(r.mded)}` : '—'}
                  </td>
                  <td className="text-right fw-700" style={{color: 'var(--pri)'}}>₹{fmt(r.net_amt)}</td>
                  <td>
                    <div className="fw-700" style={{color: 'var(--pri)'}}>{getRentMonth(month, r.pay_nature)}</div>
                    <span className={r.pay_nature === 'Adv' ? 'type-adv' : 'type-cur'}>
                      {r.pay_nature === 'Adv' ? 'Prepaid' : 'Postpaid'}
                    </span>
                  </td>
                  <td className="text-sm">
                    <div className="fw-500">{r.bank_name}</div>
                    <div className="mono">{r.account_no}</div>
                    <div className="mono text-muted">{r.ifsc}</div>
                  </td>
                  <td>
                    {r.confirmation?.confirmedAt ? (
                      <div>
                        <div className="fw-700 text-sm">{new Date(r.confirmation.confirmedAt).toLocaleDateString('en-IN')}</div>
                        <div className="text-xs text-muted">{new Date(r.confirmation.confirmedAt).toLocaleTimeString('en-IN', {hour:'2-digit',minute:'2-digit'})}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    {locked ? (
                      issues.length > 0 ? issues.map(i => <span key={i} className="issue-tag">{i}</span>)
                        : <span className="text-green text-sm">✓</span>
                    ) : (
                      <button className={`issue-btn ${issues.length ? 'has' : ''}`} onClick={() => openFlagModal(r)}>
                        {issues.length ? `${issues.length} issue${issues.length > 1 ? 's' : ''}` : '⚑ Flag'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer totals + submit */}
      <div className="mgr-footer">
        <div className="mgr-totals">
          ☑ Selected: <strong>{selCount}/{rows.length}</strong> &nbsp;·&nbsp;
          Rent: <strong>₹{fmt(selRent)}</strong> &nbsp;·&nbsp;
          TDS: <strong style={{color: 'var(--red)'}}>₹{fmt(selTds)}</strong> &nbsp;·&nbsp;
          GST: <strong style={{color: 'var(--amb)'}}>₹{fmt(selGst)}</strong> &nbsp;·&nbsp;
          Ded: <strong style={{color: 'var(--red)'}}>₹{fmt(selDed)}</strong> &nbsp;·&nbsp;
          Net Payable: <strong className="text-green">₹{fmt(selNet)}</strong>
        </div>
        {rows.some(r => r.status === 'pending') && (
          <button className="btn btn-g btn-lg" onClick={handleSubmit}>✔ Submit Confirmation</button>
        )}
      </div>

      {/* Flag issue modal */}
      {flagModal && (
        <div className="overlay" onClick={e => e.target.className === 'overlay' && setFlagModal(null)}>
          <div className="modal" style={{maxWidth: 400}}>
            <h3>Report Issue</h3>
            <p className="text-sm text-muted">Select all that apply:</p>
            {ISSUES.map((iss, idx) => (
              <label key={idx} className="flag-option">
                <input type="checkbox" checked={!!flagSelections[idx]}
                  onChange={e => setFlagSelections(prev => ({...prev, [idx]: e.target.checked}))} />
                {iss}
              </label>
            ))}
            <div className="modal-act"><button className="btn btn-p" onClick={saveFlagIssues}>Done</button></div>
          </div>
        </div>
      )}

      {/* Submit confirmation modal */}
      {submitModal && (
        <div className="overlay" onClick={e => e.target.className === 'overlay' && setSubmitModal(null)}>
          <div className="modal" style={{maxWidth: 420, textAlign: 'center'}}>
            <div style={{fontSize: 40}}>📋</div>
            <h3>Confirm Submission</h3>
            <div className="confirm-box confirm-yes">
              <div>✅ <strong>{submitModal.yC} godown(s)</strong> marked <strong>YES</strong></div>
              <div className="confirm-amt text-green">Net payable: ₹{fmt(submitModal.yA)}</div>
            </div>
            {submitModal.nC > 0 && (
              <div className="confirm-box confirm-no">
                <div>❌ <strong>{submitModal.nC} godown(s)</strong> marked <strong>NO</strong></div>
                <div className="text-muted">Amount not payable: ₹{fmt(submitModal.nA)}</div>
              </div>
            )}
            <p className="text-red text-sm fw-600">⚠ This action cannot be undone. Contact Admin for changes.</p>
            <div className="modal-act-center">
              <button className="btn btn-o" onClick={() => setSubmitModal(null)}>← Go Back</button>
              <button className="btn btn-g btn-lg" onClick={confirmSubmit} disabled={submitting}>
                {submitting ? 'Saving...' : '✅ Confirm & Lock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
